/* eslint-disable max-len */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */

import React from "react";
import * as dateFns from "date-fns";
import * as _ from "lodash";

import jwtDecode from "jwt-decode";

import IGraphQLExtensionError from "src/store/interfaces/IGraphQLExtensionError";

import {
  ECommandResultStatus,
  UserType,
  TenantType,
  RoleType,
  ModuleType,
  UserRoleType,
  ClaimType,
  ClaimGroupType,
  PermissionGroupType,
  RoleClaimType,
  UserClaimType,
  UserModuleAccessType
} from "./types.graphql";

// Utils Types
type TNonEmptyArray<T> = [T, ...T[]];
type TJoin<K, P> = K extends string | number ? P extends string | number
  ? `${K}${"" extends P ? "" : "."}${P}` : never : never;
type TModeForm = "UPDATE" | "CREATE" | "VIEW";
type TModeFormDefault = "CREATE" | "EDIT";

// Entity Types (keyof) - OMIT object / array properties
type TTenant = keyof Omit<TenantType, "users" | "roles" | "modules" | "claims" | "claimGroups" | "permissionGroups" | "roleClaims" | "userRoles" | "__typename">;
type TUser = keyof Omit<UserType, "tenant" | "userRoles" | "userClaims" | "userModuleAccesses" | "__typename">;
type TUserModuleAccess = keyof Omit<UserModuleAccessType, "tenant" | "user" | "module" | "__typename">;
type TRole = keyof Omit<RoleType, "tenant" | "module" | "userRoles" | "roleClaims" | "__typename">;
type TModule = keyof Omit<ModuleType, "tenant" | "roles" | "permissionGroups" | "__typename">;
type TUserRole = keyof Omit<UserRoleType, "tenant" | "role" | "user" | "__typename">;
type TRoleClaim = keyof Omit<RoleClaimType, "tenant" | "role" | "claim" | "__typename">;
type TUserClaim = keyof Omit<UserClaimType, "tenant" | "user" | "claim" | "__typename">;
type TClaim = keyof Omit<ClaimType, "tenant" | "roleClaims" | "userClaims" | "claimGroup" | "__typename">;
type TClaimGroup = keyof Omit<ClaimGroupType, "tenant" | "permissionGroup" | "claims" | "__typename">;
type TPermissionGroup = keyof Omit<PermissionGroupType, "tenant" | "module" | "claimGroups" | "__typename">;

class Utils {

  /** Build a GQL query string from a GraphQL query array
   * @param {string} args - The GraphQL query array
   */
  buildQuery = (...args: any) => {

    const set = (a: any, o: any = {}) => {

      const k = a.shift();

      o[k] = a.length ? set(a, o[k]) : null;

      return o;
    };

    const query = args.reduce((o: any, a: any) => set(a.split("."), o), {});

    return JSON.stringify(query)
      .replace(/"|:|null/g, "")
      .replace(/^\{/, "")
      .replace(/}$/, "");
  };

  /** Format string currency value to number
   * @param value Currency value
   *
   * 1. Replace all dots (.) with commas (,)
   * 2. Replace last comma (,) with dot (.)
   * 3. Replace all commas (,) with empty
   */
  formatMaskStringToNumber = (value: string): number => {
    if (!value) return 0;

    const negative = value.includes("-") && !value.includes("+");
    const replacedValue = Number(value.replace(/\./g, ",").replace(/,([^,]*)$/, ".$1").replace(/[^\d.]/g, ""));

    return negative ? replacedValue * -1 : replacedValue;
  };

  /** Remove special characters from a string
   * @param str The string to clean
   * @returns The cleaned string
   */
  removeSpecialChars = (str: string) => str.replace(/[^a-zA-Z0-9]/g, "");

  /** Simulate click action on a DOM element (First occurrence)
   * @param buttonClass The class of the button
   */
  clickButtonDomElement = (buttonClass: string) => {

    const button = document.getElementsByClassName(buttonClass)[0] as HTMLButtonElement;

    if (button) button.click();
  };

  /** Used to pass a date value to a format date (Used in Yup date validation) */
  parseDateString(_value: any, originalValue: any) {
    return dateFns.isDate(originalValue)
      ? originalValue
      : dateFns.parse(originalValue, "yyyy-MM-dd", new Date());
  }

  /**
   * Used to build queries and mutation (GraphQL)
   * @param field The field to be built
   * @param nestedString String used to concatenate the nested fields
   */
  getQueryFields = (field: any, nestedString = "") => {
    const fields = [...field];

    if (nestedString === "") return fields.map((currentField) => currentField);

    return fields.map((currentField) => `${nestedString}.${currentField}`);
  };

  /**
   * Decrypt a JWT token
   * @param token The token to be decrypted
   */
  decryptJwtToken = (token: string) => jwtDecode(token);

  /**
   * Get GraphQL mutation errors
   * @param mutation The mutation to extract the errors
   * @returns A vector with the errors
   */
  getMutationErrors = (mutation: any) => {
    const extensions = mutation.errors?.map((error: any) => error.extensions);
    const errors: IGraphQLExtensionError[] = [];

    extensions?.forEach((extension: any) => {
      const error = extension as IGraphQLExtensionError;

      error.data = {
        status: error?.data?.status ?? ECommandResultStatus.ERROR,
        errorCode: error?.data?.errorCode ?? "9999"
      };

      errors.push(error);
    });

    return errors;
  };

}

// region Enumerable Commands Result
export type TEnumerableCommandResult = "EnumerableCommandResult_Module";
// endregion Enumerable Commands Result

// region Commands Result
export type TCommandResult =
  "CommandResult_Void"
  | "CommandResult_Tenant"
  | "CommandResult_User"
  | "CommandResult_Role"
  | "CommandResult_Module"
  | "CommandResult_UserRole"
  | "CommandResult_Claim"
  | "CommandResult_ClaimGroup"
  | "CommandResult_PermissionGroup"
  | "CommandResult_RoleClaim"
  | "CommandResult_UserClaim"
  | "CommandResult_UserModuleAccess"
// endregion Commands Result

// region List Commands Result
export type TListCommandResult =
  "ListCommandResult_User"
  | "ListCommandResult_Tenant"
  | "ListCommandResult_Role"
  | "ListCommandResult_Module"
  | "ListCommandResult_UserRole"
  | "ListCommandResult_Claim"
  | "ListCommandResult_ClaimGroup"
  | "ListCommandResult_PermissionGroup"
  | "ListCommandResult_RoleClaim"
  | "ListCommandResult_UserClaim"
// endregion List Commands Result

export default new Utils();
export type {
  TNonEmptyArray,
  TJoin,
  TModeForm,
  TModeFormDefault,
  TUser,
  TTenant,
  TRole,
  TModule,
  TUserRole,
  TClaim,
  TClaimGroup,
  TPermissionGroup,
  TRoleClaim,
  TUserClaim,
  TUserModuleAccess
};